import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { ShieldCheck, AlertCircle, Loader2 } from 'lucide-react';

export const CallbackPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { handleLogtoCallback } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const isProcessingRef = useRef<boolean>(false);

  useEffect(() => {
    if (isProcessingRef.current) return;
    isProcessingRef.current = true;

    const processCallback = async () => {
      const code = searchParams.get('code');
      const state = searchParams.get('state');
      const errorParam = searchParams.get('error');
      const errorDesc = searchParams.get('error_description');

      if (errorParam) {
        setError(`Logto error: ${errorParam} - ${errorDesc || ''}`);
        return;
      }

      if (!code || !state) {
        setError('Missing required code or state parameter from Logto OIDC callback');
        return;
      }

      try {
        await handleLogtoCallback(code, state);
        navigate('/dashboard', { replace: true });
      } catch (err: any) {
        setError(err.message || 'Logto token exchange and verification failed');
      }
    };

    processCallback();
  }, [searchParams, handleLogtoCallback, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-950 px-4 text-slate-100 font-sans">
      <div className="w-full max-w-md bg-slate-900/90 backdrop-blur-xl p-8 rounded-3xl border border-slate-800 text-center space-y-6 shadow-2xl">
        {!error ? (
          <>
            <div className="inline-flex p-4 rounded-full bg-cyan-500/10 border border-cyan-500/30 text-cyan-400 animate-pulse">
              <Loader2 className="w-8 h-8 animate-spin" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Authenticating with Logto</h2>
              <p className="text-xs text-slate-400 mt-1">Exchanging authorization code & verifying ID token...</p>
            </div>
          </>
        ) : (
          <>
            <div className="inline-flex p-4 rounded-full bg-red-500/10 border border-red-500/30 text-red-400">
              <AlertCircle className="w-8 h-8" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">Authentication Failed</h2>
              <p className="text-xs text-red-400 mt-2 bg-red-950/60 p-3 rounded-xl border border-red-800/40">
                {error}
              </p>
            </div>
            <button
              onClick={() => navigate('/login')}
              className="w-full py-3 bg-slate-800 hover:bg-slate-700 text-white rounded-xl text-xs font-semibold transition-all border border-slate-700"
            >
              Return to Login
            </button>
          </>
        )}
      </div>
    </div>
  );
};
